import React, { useState, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { revokeScope } from "./accessManagerApi";
import "./AccessManagerPanel.css";

/**
 * RevokeDialog — confirmation modal for removing all scopes from a user.
 *
 * Displays a warning with the target user email, requires the admin to type
 * that email exactly to enable confirmation, and requires notes documenting
 * the reason. On confirm, revokeScope is called sequentially for each of the
 * user's current scopes, halting on the first failure.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is visible.
 * @param {{ email: string, scopes: string[], granted_by: string|null }} props.user - The user to revoke.
 * @param {() => void} props.onClose - Called when the dialog should close.
 * @param {(email: string, notes: string) => Promise<void>} props.onConfirm - Called on successful revocation.
 */
export default function RevokeDialog({ open, user, onClose, onConfirm }) {
  const [confirmEmail, setConfirmEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setConfirmEmail("");
      setNotes("");
      setError(null);
    }
  }, [open]);

  const targetEmail = user?.email ?? "";
  const emailMatches = confirmEmail === targetEmail && targetEmail !== "";

  const handleConfirm = async () => {
    setError(null);

    if (!emailMatches) {
      setError("Typed email does not match the target user email.");
      return;
    }

    if (!notes.trim()) {
      setError("Notes are required.");
      return;
    }

    setLoading(true);

    try {
      // Revoke each scope sequentially, halting on the first failure
      for (const scope of user.scopes || []) {
        await revokeScope(targetEmail, scope, notes.trim());
      }

      if (onConfirm) {
        await onConfirm(targetEmail, notes.trim());
      }
      onClose();
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to revoke access.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="access-manager-dialog__overlay" />
        <Dialog.Content className="access-manager-dialog__content">
          <Dialog.Title className="access-manager-dialog__title">
            Revoke All Access
          </Dialog.Title>
          <Dialog.Description className="access-manager-dialog__description">
            This removes every scope from <strong>{targetEmail}</strong>.
          </Dialog.Description>

          <div className="access-manager-dialog__warning" role="note">
            This action cannot be undone. All{" "}
            {(user.scopes || []).length} scope
            {(user.scopes || []).length === 1 ? "" : "s"} will be revoked.
          </div>

          <div className="access-manager-dialog__field">
            <label
              htmlFor="revoke-confirm-email"
              className="access-manager-dialog__notes-label"
            >
              Type <strong>{targetEmail}</strong> to confirm{" "}
              <span aria-hidden="true">*</span>
            </label>
            <input
              id="revoke-confirm-email"
              type="text"
              className="access-manager-dialog__email-input"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={targetEmail}
              required
              disabled={loading}
              autoComplete="off"
            />
          </div>

          <div className="access-manager-dialog__notes">
            <label
              htmlFor="revoke-notes"
              className="access-manager-dialog__notes-label"
            >
              Notes <span aria-hidden="true">*</span>
            </label>
            <textarea
              id="revoke-notes"
              className="access-manager-dialog__notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason for revoking access (required)"
              required
              disabled={loading}
              rows={3}
            />
          </div>

          {error && (
            <div className="access-manager-panel__error" role="alert">
              {error}
            </div>
          )}

          <div className="access-manager-dialog__actions">
            <Dialog.Close asChild>
              <button
                type="button"
                className="access-manager-dialog__btn access-manager-dialog__btn--cancel"
                disabled={loading}
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              className="access-manager-dialog__btn access-manager-dialog__btn--confirm"
              onClick={handleConfirm}
              disabled={loading || !emailMatches}
            >
              {loading ? "Revoking…" : "Revoke All"}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
